import { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQ_DATA = [
  {
    category: 'Membership & Accounts',
    questions: [
      {
        question: "What's the difference between a Free account and a paid membership?",
        answer: "A Free account gives you access to your dashboard, the pricing page, and our referral program. You cannot access the Content Library, DJ Community, or Coaching sections.\n\nPaid memberships (Starter, Pro, Elite) unlock tiered access to lessons, resources, community rooms, and coaching features depending on your subscription level."
      },
      {
        question: "What do I get with each membership tier?",
        answer: `**Starter (£12.99/month)**
• Starter-tier lessons in the Content Library
• Selected resources (no eBooks or SFX packs)
• DJ Community: Beginner Lab, Local DJ Connections, General DJ Chat
• Referral section
• No coaching access

**Pro (£25.99/month)**
• Starter + Pro lessons
• All resources including eBooks and SFX packs
• DJ Community: all community rooms
• Referral section
• No coaching access

**Elite (£79.99/month)**
• All lessons and resources
• DJ Community: all community rooms
• Coaching section: book sessions, manage mixes, view my sessions
• Referral section`
      },
      {
        question: "What is \"Lifetime Starter\" access?",
        answer: "Lifetime Starter is a one-time purchase (£45.99) that gives permanent access to Starter-tier lessons, resources, and select community rooms (Beginner Lab, Local DJ Connections, General DJ Chat). This access does not expire, but it does not include subscription benefits such as coaching, Pro/Elite content, eBooks, or SFX packs."
      }
    ]
  },
  {
    category: 'Course Purchase',
    questions: [
      {
        question: "Can I buy the course if I'm a Free account user?",
        answer: "Yes! If you buy the course for £45.99, your account becomes a Lifetime Starter. This gives you permanent access to:\n\n• Starter-tier lessons and resources (no eBooks or SFX packs)\n• DJ Community access (Beginner Lab, Local DJ Connections, General DJ Chat)\n\nThere are no recurring charges."
      },
      {
        question: "Can I buy the course if I'm already a paid subscriber?",
        answer: `Yes, but please note the following important changes:

**If a current Starter, Pro, or Elite member buys the course for £45.99:**
• Your subscription is ended immediately
• Your account becomes a Lifetime Starter
• You get permanent access to Starter-tier lessons, resources, and DJ Community access (Beginner Lab, Local DJ Connections, General DJ Chat)

**All subscription-only benefits are removed:**
• Additional chat rooms (Mix Feedback, DJ Growth, Track Discovery)
• 1-1 coaching
• Mix feedback
• Pro/Elite content
• eBooks and SFX packs

**No refunds or partial credits for any unused subscription time.**`
      },
      {
        question: "Will I be charged again after buying the course?",
        answer: "No. The course is a one-time payment. You will not have recurring charges. If you already have an active subscription, it will be ended immediately when you purchase the course."
      },
      {
        question: "Who should buy the Lifetime Starter course?",
        answer: `The Lifetime Starter course is ideal for:

• Beginners who want permanent access to Starter-level lessons
• Users who want to learn at their own pace without recurring fees
• Those who don't need coaching, Pro/Elite content, eBooks, or SFX packs
• DJs who prefer a one-time investment over monthly payments`
      },
      {
        question: "Can I get a refund if I buy the course while subscribed?",
        answer: "No. Purchasing the course while subscribed ends your current membership immediately, and there are no refunds or credits for remaining subscription time. Please confirm carefully before purchasing."
      }
    ]
  },
  {
    category: 'Cancellation & Access',
    questions: [
      {
        question: "What happens if I cancel my paid subscription?",
        answer: `If you cancel your Starter, Pro, or Elite subscription:

• Your account reverts to Free immediately
• You lose all course access and subscription benefits immediately
• Coaching sessions, Pro/Elite content, and additional community rooms are no longer available
• Content Library access is removed
• DJ Community access is removed`
      },
      {
        question: "Do I lose my course access if my subscription ends?",
        answer: `It depends on your account type:

• **Free Account:** No course access
• **Paid Subscription (cancelled):** Yes, all tiered access ends immediately
• **Lifetime Starter:** No, your Starter content access is permanent and never expires`
      },
      {
        question: "What happens if I cancel near a billing cycle?",
        answer: "If you cancel your subscription within 2 days of your next billing cycle, a payment may still be processed due to bank or direct debit timing. In this case, please notify your bank or stop your direct debit to prevent further charges."
      },
      {
        question: "Can I upgrade my Lifetime Starter to Pro or Elite later?",
        answer: "Yes. To regain subscription benefits like coaching, Pro/Elite lessons, eBooks, SFX packs, or additional community rooms, you would need to subscribe to the relevant membership tier again. Your Lifetime Starter content will remain permanent even if you subscribe to a higher tier."
      }
    ]
  }
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  // Simple markdown-like formatting for the answer
  const formatAnswer = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-bold mb-2" style={{ color: '#D4AF37' }}>{line.slice(2, -2)}</p>;
      }
      if (line.startsWith('• ')) {
        return <li key={i} className="ml-4 mb-1">{line.slice(2)}</li>;
      }
      if (line === '') {
        return <br key={i} />;
      }
      return <p key={i} className="mb-2">{line}</p>;
    });
  };

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 px-2 text-left transition-colors hover:bg-white/5"
        data-testid="faq-toggle"
      >
        <span className="font-medium pr-4" style={{ color: '#ffffff' }}>{question}</span>
        {isOpen ? (
          <ChevronUp size={20} style={{ color: '#D4AF37' }} className="flex-shrink-0" />
        ) : (
          <ChevronDown size={20} style={{ color: '#D4AF37' }} className="flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 px-2 text-sm leading-relaxed" style={{ color: '#a3a3a3' }}>
          {formatAnswer(answer)}
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const expandAll = () => {
    const allOpen = {};
    FAQ_DATA.forEach((category, catIndex) => {
      category.questions.forEach((_, qIndex) => {
        allOpen[`${catIndex}-${qIndex}`] = true;
      });
    });
    setOpenItems(allOpen);
  };

  const collapseAll = () => {
    setOpenItems({});
  };

  return (
    <div className="min-h-screen py-24" data-testid="faq-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(212, 175, 55, 0.2)' }}>
            <HelpCircle size={32} style={{ color: '#D4AF37' }} />
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#ffffff' }}>
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            Everything you need to know about memberships, course access, and billing
          </p>
          
          {/* Expand/Collapse buttons */}
          <div className="flex justify-center gap-4">
            <button
              onClick={expandAll}
              className="text-sm px-4 py-2 rounded-full transition-colors hover:bg-white/10"
              style={{ color: '#D4AF37', border: '1px solid rgba(212, 175, 55, 0.3)' }}
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="text-sm px-4 py-2 rounded-full transition-colors hover:bg-white/10"
              style={{ color: '#a3a3a3', border: '1px solid rgba(163, 163, 163, 0.3)' }}
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {FAQ_DATA.map((category, catIndex) => (
            <div key={catIndex} className="glass-panel rounded-2xl border overflow-hidden">
              <div className="px-6 py-4 border-b border-border" style={{ background: 'rgba(212, 175, 55, 0.05)' }}>
                <h2 className="text-xl font-bold" style={{ color: '#D4AF37' }}>
                  {category.category}
                </h2>
              </div>
              <div className="px-4">
                {category.questions.map((item, qIndex) => (
                  <FAQItem
                    key={qIndex}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openItems[`${catIndex}-${qIndex}`]}
                    onToggle={() => toggleItem(catIndex, qIndex)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-12 text-center glass-panel rounded-2xl p-8 border">
          <h3 className="text-xl font-bold mb-4" style={{ color: '#ffffff' }}>
            Still have questions?
          </h3>
          <p className="text-muted-foreground mb-6">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <a href="mailto:contact@blendonics.com">
            <button 
              className="px-8 py-3 rounded-full font-semibold transition-all hover:scale-105"
              style={{ background: '#D4AF37', color: '#141414' }}
            >
              Contact Support
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
